import { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import type { UseNavigateResult } from '@tanstack/react-router';

import { useMe } from '@/api/me';
import { useMyTenants } from '@/api/tenant';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { useCreateEstate } from '@/features/estates/api/estates';
import { EstateForm } from '@/features/estates/components/estate-form';
import { type EstateSchemaType, getCreateEstateSchema } from '@/features/estates/schemas/estate';
import type { UpdateEstateAreas } from '@/features/estates-property/api/estate-areas';
import { useUpdateEstateAreas } from '@/features/estates-property/api/estate-areas';
import { useUpdateEstateDetails } from '@/features/estates-property/api/estate-details';
import type { Project } from '@/features/projects/api/projects';
import { getProject } from '@/features/projects/api/projects';
import { useCreateTenantProject } from '@/features/tenants/api/tenant-projects';
import type { AtLeastOne } from '@/utils/types/helpers';

import { ModalBase } from './modals-base';

export interface CreateEstateModalProps {
  navigate: UseNavigateResult<string>;
  onSuccess?: (object: AtLeastOne<{ estateId?: string; projectId?: string }>) => void;
}

export const CreateEstateModal: React.FC<CreateEstateModalProps> = ({ navigate, onSuccess }) => {
  const schema = getCreateEstateSchema();
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: tenants } = useMyTenants();
  const projectDataRef = useRef<Project | null>(null);
  const queryClient = useQueryClient();
  const { mutateAsync: updateEstateDetails } = useUpdateEstateDetails({});

  const { mutateAsync: updateEstateAreas } = useUpdateEstateAreas({});

  const { mutateAsync: createEstateAsync } = useCreateEstate({
    mutationConfig: {
      onSuccess: async ([createdEstate]) => {
        const projectData = projectDataRef.current;
        if (projectData && createdEstate.projectId) {
          const promises: Promise<unknown>[] = [];
          if (projectData.details) {
            promises.push(
              updateEstateDetails({
                id: createdEstate.id,
                constructionType: '',
                condition: '',
                buildingYear: projectData.details.buildingYear || '',
                heatingType: [],
                heatGeneration: '',
                marketType: '',
                usageType: projectData.details.usageType || '',
                typeOfGlass: '',
                flooring: '',
                propertyType: '',
              })
            );
          }
          if (projectData.areaRooms) {
            // Seed areas with neutral defaults to avoid DB defaults (e.g., rooms=1)
            const updatedAreas: UpdateEstateAreas = {
              id: createdEstate.id,
              totalNumberOfRooms: 0,
            };
            if (
              projectData?.areaRooms.hasElevator !== null &&
              projectData.areaRooms.hasElevator !== undefined
            ) {
              updatedAreas.hasElevator = projectData.areaRooms.hasElevator;
            }
            if ('hasElevator' in updatedAreas) {
              promises.push(updateEstateAreas(updatedAreas));
            }
          }
          await Promise.all(promises);
        }
        if (onSuccess) {
          onSuccess({ estateId: createdEstate.id });
        } else {
          navigate({ to: '/estates/$estateId', params: { estateId: createdEstate.id } });
        }
        toast.success(t('create-estate-success'));
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const { mutateAsync: createProjectAsync } = useCreateTenantProject({
    mutationConfig: {
      onSuccess: ([createdProject]) => {
        if (onSuccess) {
          onSuccess({ projectId: createdProject.id });
        } else {
          navigate({ to: '/projects/$projectId', params: { projectId: createdProject.id } });
        }
        toast.success(t('create-project-success'));
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const methods = useForm<EstateSchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      estateData: {
        name: '',
        propertyType: '',
        marketType: '',
        tenantId: tenants[0]?.id ?? '',
        projectId: undefined,
        creatorId: me?.id,
        propertyLevel: PROPERTY_LEVELS.OBJECT,
      },
      addressData: {
        address: '',
        postalCode: '',
        city: '',
        countryId: '',
        street: '',
        houseNumber: '',
      },
    },
  });

  const projectId = methods.watch('estateData.projectId') || '';

  const onSubmit = async (args: EstateSchemaType) => {
    projectDataRef.current = null;
    if (args.estateData.tenantId && args.estateData.propertyLevel === PROPERTY_LEVELS.PROJECT) {
      await createProjectAsync({
        projectData: {
          ...args.estateData,
          status: 'draft',
          isActive: 0,
          creatorId: me?.id,
        },
        addressData: {
          ...args.addressData,
        },
        tenantId: args.estateData.tenantId,
      });
    } else {
      if (args.estateData.projectId) {
        const projectData = await getProject(args.estateData.projectId);
        projectDataRef.current = projectData;
        args.estateData = {
          ...args.estateData,
          ...(projectData?.energyDetails?.id && {
            estateEnergyDetailsId: projectData.energyDetails.id,
          }),
        };
      }
      await createEstateAsync(args);
      queryClient.invalidateQueries({ queryKey: ['projects', 'estates', projectId] });
    }
  };

  return (
    <ModalBase>
      <FormProvider {...methods}>
        <EstateForm label={t('create')} onSubmit={onSubmit} />
      </FormProvider>
    </ModalBase>
  );
};
